import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { blogPosts } from '../mockData';

const BlogDetail = () => {
  const { blogSlug } = useParams();
  const navigate = useNavigate();
  const blog = blogPosts.find(b => b.slug === blogSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [blogSlug]);

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
          <Button onClick={() => navigate('/blog')} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            Go Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative h-[500px] overflow-hidden">
        <img 
          src={blog.image} 
          alt={blog.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-indigo-900/70"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-6">
            <Button 
              onClick={() => navigate('/blog')}
              variant="outline"
              className="mb-6 text-white border-white hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
            <div className="flex items-center space-x-6 mb-6 text-white">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>{blog.date}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>{blog.readTime}</span>
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 max-w-4xl">{blog.title}</h1>
            <p className="text-xl text-white/90 max-w-3xl">{blog.excerpt}</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Share Buttons */}
            <div className="flex items-center justify-between mb-12 pb-8 border-b-2 border-gray-200">
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="font-semibold">Share this article:</span>
              </div>
              <Button variant="outline" size="sm" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>

            {/* Blog Content */}
            <article className="prose prose-lg max-w-none">
              <div className="space-y-6 text-gray-700 leading-relaxed">
                {blog.content.map((paragraph, index) => (
                  <p key={index} className="text-lg">{paragraph}</p>
                ))}
              </div>

              {blog.sections && blog.sections.map((section, index) => (
                <div key={index} className="mt-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">{section.heading}</h2>
                  <div className="space-y-4">
                    {section.paragraphs.map((para, idx) => (
                      <p key={idx} className="text-lg text-gray-700 leading-relaxed">{para}</p>
                    ))}
                  </div>
                  {section.bullets && (
                    <ul className="mt-6 space-y-3">
                      {section.bullets.map((bullet, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <span className="text-blue-600 font-bold mt-1">•</span>
                          <span className="text-lg text-gray-700">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </article>

            {/* Call to Action */}
            <div className="mt-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 text-center text-white">
              <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Business?</h3>
              <p className="text-xl mb-8 text-white/90">Let's discuss how we can help you achieve your goals with innovative technology solutions.</p>
              <Button 
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                Contact Us Today
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;